import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import {
  BanknotesIcon,
  DocumentTextIcon,
  ClipboardDocumentListIcon,
} from '@heroicons/react/24/outline';
import { DashboardSection } from './DashboardSection';
import { Heading, Text } from '../../design-system/components/Typography';
import { Stack, Flex } from '../../design-system/components/Layout';
import { spacing } from '../../design-system/tokens/spacing';
import { typography } from '../../design-system/tokens/typography';
import { DashboardActivityItem } from '../../types/dashboard';

const ViewAllLink = styled.button`
  background: none;
  border: none;
  padding: 0;
  font-size: ${typography.fontSize.sm};
  font-weight: ${typography.fontWeight.medium};
  color: ${props => props.theme.colors.primary};
  cursor: pointer;
  font-family: ${typography.fontFamily.primary};
  &:hover {
    text-decoration: underline;
  }
  &:focus-visible {
    outline: 2px solid ${props => props.theme.colors.primary};
    outline-offset: 2px;
  }
`;

const ActivityList = styled.ul`
  list-style: none;
  margin: 0;
  padding: 0;
`;

const ActivityRow = styled.li`
  display: flex;
  gap: ${spacing[3]};
  padding: ${spacing[4]} 0;
  border-bottom: 1px solid ${props => props.theme.colors.border};
  &:last-child {
    border-bottom: none;
    padding-bottom: 0;
  }
  &:first-child {
    padding-top: 0;
  }
`;

const IconBox = styled.div`
  flex-shrink: 0;
  width: 34px;
  height: 34px;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: ${props => props.theme.colors.surfaceHover};
  color: ${props => props.theme.colors.textMuted};
`;

const ItemButton = styled.button`
  flex: 1;
  display: flex;
  gap: ${spacing[3]};
  align-items: flex-start;
  background: none;
  border: none;
  padding: 0;
  text-align: left;
  cursor: pointer;
  font-family: inherit;
  width: 100%;
  &:focus-visible {
    outline: 2px solid ${props => props.theme.colors.primary};
    outline-offset: 2px;
    border-radius: 4px;
  }
`;

const StatusPill = styled.span<{ $variant?: string }>`
  display: inline-block;
  margin-top: ${spacing[1]};
  padding: 2px ${spacing[2]};
  border-radius: 4px;
  font-size: ${typography.fontSize.xs};
  font-weight: ${typography.fontWeight.medium};
  background: ${props => {
    switch (props.$variant) {
      case 'success':
        return props.theme.colors.successBg ?? `${props.theme.colors.success}18`;
      case 'error':
        return props.theme.colors.errorBg ?? `${props.theme.colors.error}18`;
      case 'warning':
        return props.theme.colors.warningBg ?? `${props.theme.colors.warning}18`;
      default:
        return props.theme.colors.surfaceHover;
    }
  }};
  color: ${props => {
    switch (props.$variant) {
      case 'success':
        return props.theme.colors.success;
      case 'error':
        return props.theme.colors.error;
      case 'warning':
        return props.theme.colors.warning;
      default:
        return props.theme.colors.textSecondary;
    }
  }};
`;

function ActivityIcon({ type }: { type: DashboardActivityItem['type'] }) {
  const props = { width: 18, height: 18, 'aria-hidden': true as const };
  switch (type) {
    case 'payment':
      return <BanknotesIcon {...props} />;
    case 'document':
      return <DocumentTextIcon {...props} />;
    case 'changeRequest':
      return <ClipboardDocumentListIcon {...props} />;
    default:
      return <DocumentTextIcon {...props} />;
  }
}

interface ActivityItemProps {
  item: DashboardActivityItem;
  onNavigate: (link: string) => void;
}

const ActivityItem: React.FC<ActivityItemProps> = ({ item, onNavigate }) => {
  const content = (
    <>
      <IconBox>
        <ActivityIcon type={item.type} />
      </IconBox>
      <div style={{ flex: 1, minWidth: 0 }}>
        <Text size="sm" weight="semibold" style={{ display: 'block' }}>
          {item.title}
        </Text>
        <span title={item.fullDescription} style={{ display: 'block', minWidth: 0 }}>
          <Text
            size="sm"
            color="secondary"
            style={{ display: 'block', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
          >
            {item.description}
          </Text>
        </span>
        <Text size="xs" color="muted" style={{ display: 'block', marginTop: spacing[1] }}>
          {item.formattedTimestamp}
        </Text>
        {item.statusLabel && (
          <StatusPill $variant={item.statusVariant}>{item.statusLabel}</StatusPill>
        )}
      </div>
    </>
  );

  if (item.link) {
    return (
      <ActivityRow>
        <ItemButton type="button" onClick={() => onNavigate(item.link!)}>
          {content}
        </ItemButton>
      </ActivityRow>
    );
  }

  return <ActivityRow>{content}</ActivityRow>;
};

interface RecentActivityCardProps {
  activities: DashboardActivityItem[];
}

export const RecentActivityCard: React.FC<RecentActivityCardProps> = ({ activities }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  return (
    <DashboardSection>
      <Stack direction="column" spacing="md">
        <Flex direction="row" justify="space-between" align="center">
          <Heading variant="h3" as="h2" style={{ fontSize: typography.fontSize.section }}>
            {t('dashboard.activity.title')}
          </Heading>
          <ViewAllLink type="button" onClick={() => navigate('/payments')}>
            {t('dashboard.activity.viewAll')}
          </ViewAllLink>
        </Flex>

        {activities.length === 0 ? (
          <Text size="sm" color="secondary">{t('dashboard.empty.activity')}</Text>
        ) : (
          <ActivityList>
            {activities.map(item => (
              <ActivityItem key={item.id} item={item} onNavigate={navigate} />
            ))}
          </ActivityList>
        )}
      </Stack>
    </DashboardSection>
  );
};

export default RecentActivityCard;
